import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import type { TeamRole } from '../types/common'

interface TeamContextValue {
  isTeamMember: boolean
  agentId: string | null
  agencyName: string | null
  teamRole: TeamRole | null
  financialAccess: boolean
  portfolioIds: string[]
  loading: boolean
}

const TeamContext = createContext<TeamContextValue>({
  isTeamMember: false,
  agentId: null,
  agencyName: null,
  teamRole: null,
  financialAccess: false,
  portfolioIds: [],
  loading: true,
})

export function TeamContextProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [isTeamMember, setIsTeamMember] = useState(false)
  const [agentId, setAgentId] = useState<string | null>(null)
  const [agencyName, setAgencyName] = useState<string | null>(null)
  const [teamRole, setTeamRole] = useState<TeamRole | null>(null)
  const [financialAccess, setFinancialAccess] = useState(false)
  const [portfolioIds, setPortfolioIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return

    if (!user) {
      setIsTeamMember(false)
      setAgentId(null)
      setAgencyName(null)
      setTeamRole(null)
      setFinancialAccess(false)
      setPortfolioIds([])
      // CRITICAL: always set loading=false so PortfolioContext's teamLoading
      // gate never leaves an owner with a stuck portfolio load.
      setLoading(false)
      return
    }

    async function resolve() {
      try {
        // Step 1: check if user is an accepted team member
        const { data: membership, error: memberError } = await supabase
          .from('team_members')
          .select('owner_id, role, permissions')
          .eq('member_id', user!.id)
          .eq('invite_status', 'accepted')
          .is('deleted_at', null)
          .maybeSingle()

        if (memberError || !membership) {
          // Not a team member (or query failed) — degrade to owner
          setIsTeamMember(false)
          setAgentId(null)
          setAgencyName(null)
          setTeamRole(null)
          setFinancialAccess(false)
          setPortfolioIds([])
          setLoading(false)
          return
        }

        const role = membership.role as TeamRole
        const fa = (membership.permissions as { financial_access?: boolean })?.financial_access === true
          || role === 'admin' // admin always has financial access

        // Step 2: resolve accessible portfolio IDs
        let pIds: string[] = []
        if (role === 'admin') {
          // Direct read of the agency owner's portfolios (an admin can read
          // them via the portfolios "Team members can view accessible" policy).
          // Replaces get_agent_portfolio_ids, which computed ownership in a
          // DEFINER (principle 8) and is dropped in migration 368.
          const { data } = await supabase
            .from('portfolios')
            .select('id')
            .eq('owner_id', membership.owner_id)
            .is('deleted_at', null)
          pIds = (data || []).map((p) => p.id as string)
        } else {
          const { data } = await supabase.rpc('get_team_member_portfolio_ids', {
            p_user_id: user!.id,
          })
          pIds = (data || []) as string[]
        }

        // Fetch agency owner's display name for UI labels
        let resolvedAgencyName: string | null = null
        try {
          const { data: agentProfile } = await supabase
            .from('profiles')
            .select('company_name, full_name')
            .eq('id', membership.owner_id)
            .single()
          resolvedAgencyName = agentProfile?.company_name || agentProfile?.full_name || null
        } catch {
          // Non-critical — agencyName stays null, UI uses fallback
        }

        setIsTeamMember(true)
        setAgentId(membership.owner_id)
        setAgencyName(resolvedAgencyName)
        setTeamRole(role)
        setFinancialAccess(fa)
        setPortfolioIds(pIds)
      } catch {
        // Any error: degrade gracefully to "treat as owner"
        setIsTeamMember(false)
        setAgentId(null)
        setAgencyName(null)
        setTeamRole(null)
        setFinancialAccess(false)
        setPortfolioIds([])
      } finally {
        // CRITICAL: loading MUST always become false — if this didn't fire,
        // PortfolioContext would hang waiting for teamLoading=false, blocking
        // the owner's portfolio load indefinitely.
        setLoading(false)
      }
    }

    resolve()
  }, [user?.id, authLoading])

  const value = useMemo<TeamContextValue>(() => ({
    isTeamMember,
    agentId,
    agencyName,
    teamRole,
    financialAccess,
    portfolioIds,
    loading,
  }), [isTeamMember, agentId, agencyName, teamRole, financialAccess, portfolioIds, loading])

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  )
}

export function useTeamContext() {
  return useContext(TeamContext)
}
